import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import Loader from '../../../../../../components/loader/Loader';
import { api as learningPathApi } from '../../../../../../api/learningPath';
import AddCourse from '../path/createPath/components/AddCourse';
import toast from 'react-hot-toast';
import PermissionGate from '../../../../../../components/PermissionGate';

const formatDate = (value?: string) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString('en-ng');
};

const getLearnerStatusChip = (status?: string) => {
  if (status === 'completed') {
    return 'bg-green-100 text-green-700';
  }
  if (status === 'in_progress') {
    return 'bg-orange-100 text-orange-700';
  }
  return 'bg-slate-100 text-slate-500';
};

const formatLearnerStatus = (status?: string) => {
  if (!status) return 'Not Started';
  return status.split('_').map((word) => `${word[0].toUpperCase()}${word.slice(1)}`).join(' ');
};

const LearningPathDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'courses' | 'learners'>('courses');
  const [addCourseModal, setAddCourseModal] = useState<boolean>(false);
  const [selectedCourses, setSelectedCourses] = useState<any[]>([]);
  const [openedAddCourseModalOnce, setOpenedAddCourseModalOnce] = useState<boolean>(false);

  const { data, isLoading, refetch: refetchDetail } = useQuery(
    ['learning-path-detail', id],
    () => learningPathApi.getIndividualLearningPath({ org_id: undefined, id }),
    {
      enabled: !!id,
      cacheTime: 100,
      staleTime: 100,
      refetchOnWindowFocus: true,
      select: (response: any) => response?.data || response,
    }
  );

  const { data: learnersResponse, isLoading: learnersLoading } = useQuery(
    ['learning-path-learners', id],
    () => learningPathApi.getLearningPathLearners({ learningPathId: id, page: 1, perPage: 1000, search: '' }),
    {
      enabled: !!id,
      cacheTime: 100,
      staleTime: 100,
      refetchOnWindowFocus: true,
      select: (response: any) => response,
    }
  );

  const assignedCourses = useMemo(() => {
    return (data?.learningPathCourses || data?.journeyCourses || data?.courses || []).map((entry: any) => entry?.course || entry);
  }, [data]);

  const learners = learnersResponse?.data || [];

  const unassignMutation = useMutation(learningPathApi.unassignCoursesFromLearningPath, {
    onSuccess: (response: any) => {
      toast.success(response?.message || 'Course unassigned successfully');
      refetchDetail();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to unassign course');
    }
  });

  const handleAddCourseModal = () => {
    setAddCourseModal((previous) => !previous);
    setOpenedAddCourseModalOnce(true);
  };

  useEffect(() => {
    if (openedAddCourseModalOnce && !addCourseModal) {
      refetchDetail();
    }
  }, [addCourseModal, openedAddCourseModalOnce]);

  const unassignCourse = (courseId: string) => {
    if (!id || !courseId) return;
    unassignMutation.mutate({
      learningPathId: id,
      payload: {
        courseIds: [courseId],
      },
    });
  };

  if (isLoading) return <Loader />;

  return (
    <div className="p-5">
      <button
        onClick={() => navigate('/admin/learning-path')}
        className="flex items-center text-gray-500 hover:text-gray-700 mb-6 font-semibold"
      >
        <i className="bi bi-chevron-left mr-2" /> Back to Learning Paths
      </button>

      <div className="bg-[#0A3059] rounded-lg text-white p-6 mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            data?.status === 'published' ? 'bg-[#D1F2D1] text-[#00984C]' : 'bg-[#FFE8CC] text-[#FF9100]'
          }`}>
            {data?.status || 'Unknown'}
          </span>
        </div>
        <h1 className="text-2xl font-bold">{data?.name || 'Learning Path'}</h1>

        <div className="bg-white text-black mt-6 rounded-lg p-6 grid grid-cols-1 md:grid-cols-3 gap-4 shadow-sm">
          <div>
            <p className="text-gray-500 text-sm mb-1">Date Range</p>
            <p className="font-semibold">
              {formatDate(data?.startDate)} - {formatDate(data?.endDate)}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Type</p>
            <p className="font-semibold capitalize">{data?.type || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Assigned Courses</p>
            <p className="font-semibold">{assignedCourses?.length || 0}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="border-b border-gray-100 pb-0 mb-4 flex items-center gap-2">
          <button
            onClick={() => setActiveTab('courses')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
              activeTab === 'courses' ? 'text-[#0A4AA6] border-[#0A4AA6]' : 'text-gray-600 border-transparent'
            }`}
          >
            Courses
          </button>
          <button
            onClick={() => setActiveTab('learners')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
              activeTab === 'learners' ? 'text-[#0A4AA6] border-[#0A4AA6]' : 'text-gray-600 border-transparent'
            }`}
          >
            Learners
          </button>
        </div>

        {activeTab === 'courses' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h5 className="text-md font-semibold text-gray-600">{assignedCourses?.length || 0} courses assigned</h5>
              <PermissionGate slug="learning-path-management.update">
                <button
                  onClick={handleAddCourseModal}
                  className="bg-primary-blue text-white px-5 py-2 rounded-xl text-sm font-semibold"
                >
                  <i className="bi bi-plus mr-2" /> Assign Courses
                </button>
              </PermissionGate>
            </div>

            {assignedCourses?.map((course: any, index: number) => {
              const modulesCount = course?.modules?.length || course?.courseModules?.length || 0;
              return (
                <div key={course?.id || index} className="flex items-center justify-between p-4 border border-gray-100 rounded-2xl">
                  <div className="flex items-center space-x-4">
                    <span className="w-8 h-8 rounded-full bg-[#0A4AA6] text-white flex items-center justify-center font-semibold text-sm">
                      {index + 1}
                    </span>
                    <div>
                      <h4 className="font-semibold text-gray-800 text-xl">{course?.name || 'Untitled Course'}</h4>
                      <p className="text-sm text-gray-500">
                        {modulesCount} modules {course?.duration ? `· ${course?.duration}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-600 capitalize">
                      {course?.status || 'published'}
                    </span>
                    <PermissionGate slug="learning-path-management.update">
                      <button
                        type="button"
                        onClick={() => unassignCourse(course?.id)}
                        className="text-red-400 text-xl"
                        title="Unassign Course"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              );
            })}
            {assignedCourses?.length === 0 && (
              <p className="text-center text-gray-500 py-4">No courses assigned yet.</p>
            )}
          </div>
        )}

        {activeTab === 'learners' && (
          <div>
            {learnersLoading ? (
              <Loader />
            ) : learners?.length > 0 ? (
              <div className="border border-gray-100 rounded-lg overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100 text-gray-600">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3 w-[20%]">Learner</th>
                      <th className="text-left font-semibold px-4 py-3 w-[32%]">Email</th>
                      <th className="text-left font-semibold px-4 py-3 w-[16%]">Department</th>
                      <th className="text-left font-semibold px-4 py-3 w-[10%]">Completed</th>
                      <th className="text-left font-semibold px-4 py-3 w-[12%]">Status</th>
                      <th className="text-left font-semibold px-4 py-3 w-[10%]">Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learners.map((learner: any, index: number) => {
                      const totalCourses = learner?.courses?.length || assignedCourses?.length || 0;
                      return (
                        <tr key={learner?.user?.id || index} className="border-b border-gray-100 last:border-b-0 align-middle">
                          <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">{learner?.user?.name || 'Learner'}</td>
                          <td className="px-4 py-3 text-gray-600 truncate" title={learner?.user?.email || 'No email'}>{learner?.user?.email || 'No email'}</td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{learner?.user?.department || '-'}</td>
                          <td className="px-4 py-3 text-gray-700 font-semibold whitespace-nowrap">{learner?.completedCoursesCount || 0}/{totalCourses}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getLearnerStatusChip(learner?.status)}`}>
                              {formatLearnerStatus(learner?.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{learner?.startedAt ? formatDate(learner?.startedAt) : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">No learner data available.</p>
            )}
          </div>
        )}
      </div>

      <PermissionGate slug="learning-path-management.update">
        <AddCourse
          addCourseModal={addCourseModal}
          handleAddCourseModal={handleAddCourseModal}
          selectedCourses={selectedCourses}
          setSelectedCourses={setSelectedCourses}
        />
      </PermissionGate>
    </div>
  );
};

export default LearningPathDetail;
